import { z } from 'zod'
import { format, startOfToday } from 'date-fns'
import type { CommitmentLetterInputs } from '@/api/commitmentLetters'

const requiredDate = (label: string) =>
  z
    .date({
      required_error: `${label} is required`,
      invalid_type_error: `${label} is required`,
    })
    .refine(d => d >= startOfToday(), `${label} cannot be in the past`)

export const contactSchema = z.object({
  name: z.string().trim().min(1, 'Name is required').max(200),
  email: z.string().trim().email('Enter a valid email'),
})

export const commitmentLetterSchema = z.object({
  dueDate: requiredDate('Due date'),
  sampleCount: z
    .number({ invalid_type_error: 'Number of samples is required' })
    .int('Use a whole number')
    .min(0, 'Cannot be negative')
    .max(1000),
  sampleDueDate: requiredDate('Sample date'),
  sampleContacts: z.array(contactSchema).min(1, 'Add at least one contact'),
  confirmationContacts: z
    .array(contactSchema)
    .min(1, 'Add at least one contact'),
  signerNames: z.string().max(500),
  /** Per-vendor Strategy Code overrides (vendor_key -> code). */
  strategyCodes: z.record(z.string()),
})

export type CommitmentLetterFormValues = z.infer<typeof commitmentLetterSchema>

export type CommitmentLetterFormInput = Omit<
  CommitmentLetterFormValues,
  'dueDate' | 'sampleDueDate'
> & {
  dueDate: Date | null
  sampleDueDate: Date | null
}

export const emptyContact = () => ({ name: '', email: '' })

export function defaultFormValues(): CommitmentLetterFormInput {
  return {
    dueDate: null,
    sampleCount: 3,
    sampleDueDate: null,
    sampleContacts: [emptyContact()],
    confirmationContacts: [emptyContact()],
    signerNames: '',
    strategyCodes: {},
  }
}

/** Fields validated before leaving each step (index = step). */
export const STEP_FIELDS: (keyof CommitmentLetterFormValues)[][] = [
  ['dueDate', 'sampleCount', 'sampleDueDate'],
  ['sampleContacts', 'confirmationContacts', 'signerNames'],
  [],
  [],
]

export function toInputs(
  values: CommitmentLetterFormValues
): CommitmentLetterInputs {
  return {
    due_date: format(values.dueDate, 'yyyy-MM-dd'),
    sample_count: values.sampleCount,
    sample_due_date: format(values.sampleDueDate, 'yyyy-MM-dd'),
    sample_contacts: values.sampleContacts,
    confirmation_contacts: values.confirmationContacts,
    signer_names: values.signerNames
      .split(',')
      .map(n => n.trim())
      .filter(Boolean),
    strategy_codes: values.strategyCodes,
  }
}
